import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { jwtDecode } from "jwt-decode";
import { saveAs } from "file-saver";
import ParentWrapper from "../../components/ParentWrapper";
import CommonFilter from "../../components/CommonFilter";
import Loader from "../../components/AnimatedLoader";
import axiosInstance from "../../services/axiosInterceptors";
import apiEndPointsConfig from "../../services/apiEndPoints";
import AgentReports from "./dashboard2/AgentReports";

const NIPPON_COMPANY_ID = "69fdafaff0596b391f8be07b";
const TRAINING_CUTOFF_DATE = "2026-07-10";

function getDefaultDateRange() {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    startDate: start.toISOString().split("T")[0],
    endDate: today.toISOString().split("T")[0],
  };
}

function SummaryCard({ label, value, helper }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "#dde3eb", height: "100%" }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Typography fontSize={11} color="#657282" fontWeight={800} textTransform="uppercase" letterSpacing="0.06em">
          {label}
        </Typography>
        <Typography mt={0.5} fontSize={24} fontWeight={900} color="#202832" lineHeight={1.15}>
          {value}
        </Typography>
        {helper && (
          <Typography mt={0.5} fontSize={12} color="#64748b">
            {helper}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function averageScoreFromBuckets(buckets) {
  const totals = buckets.reduce(
    (acc, bucket) => {
      const count = bucket.count || 0;
      acc.calls += count;
      acc.score += (bucket.score || 0) * count;
      return acc;
    },
    { calls: 0, score: 0 }
  );
  return {
    calls: totals.calls,
    score: totals.calls ? totals.score / totals.calls : 0,
  };
}

function buildWeightedRows(agentRows) {
  return agentRows.map((agent) => {
    const trend = agent.trend || [];
    const recent = averageScoreFromBuckets(
      trend
        .filter((day) => (day.totalCalls || 0) > 0 && String(day.date) >= TRAINING_CUTOFF_DATE)
        .map((day) => ({ count: day.totalCalls || 0, score: day.averageCallScore || 0 }))
    );
    const older = averageScoreFromBuckets(
      trend
        .filter((day) => (day.totalCalls || 0) > 0 && String(day.date) < TRAINING_CUTOFF_DATE)
        .map((day) => ({ count: day.totalCalls || 0, score: day.averageCallScore || 0 }))
    );
    const weightedScore =
      recent.calls && older.calls
        ? recent.score * 0.65 + older.score * 0.35
        : recent.calls
          ? recent.score
          : older.score;

    return {
      agentName: agent.agentName,
      totalCalls: agent.totalCalls || 0,
      recentCalls: recent.calls,
      recentScore: recent.score,
      olderCalls: older.calls,
      olderScore: older.score,
      weightedScore,
    };
  });
}

function getDateLabels(startDate, endDate) {
  const dates = [];
  const current = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  while (current <= end) {
    dates.push(formatLocalDate(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getFirstCallDate(agentRows, fallbackDate) {
  const dates = agentRows
    .flatMap((agent) => agent.trend || [])
    .filter((day) => (day.totalCalls || 0) > 0 && day.date)
    .map((day) => day.date)
    .sort();
  return dates[0] || fallbackDate;
}

function ordinalDay(day) {
  const suffix = day > 3 && day < 21 ? "th" : ["th", "st", "nd", "rd"][day % 10] || "th";
  return `${day}${suffix}`;
}

function formatExcelDate(dateStr) {
  const date = new Date(`${dateStr}T00:00:00`);
  return `${ordinalDay(date.getDate())} ${date.toLocaleString("en-IN", { month: "long" })}`;
}

function escapeCell(value) {
  return String(value ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function exportMisExcel(agentRows, startDate, endDate) {
  const dateLabels = getDateLabels(startDate, endDate);
  const { rows, columnTotals, grandTotal } = buildMisRows(agentRows, dateLabels);

  const header = ["Sno", "BM name", ...dateLabels.map(formatExcelDate), "Total"];
  const tableRows = [
    header,
    ...rows.map((row) => [row.sno, row.bmName, ...row.counts, row.total]),
    ["", "Total", ...columnTotals, grandTotal],
  ];

  const html = `
    <html>
      <head><meta charset="utf-8" /></head>
      <body>
        <table border="1">
          ${tableRows
            .map(
              (row, rowIndex) =>
                `<tr>${row
                  .map((cell) => `<${rowIndex === 0 ? "th" : "td"}>${escapeCell(cell)}</${rowIndex === 0 ? "th" : "td"}>`)
                  .join("")}</tr>`
            )
            .join("")}
        </table>
      </body>
    </html>
  `;
  const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
  saveAs(blob, `BM_Day_Wise_MIS_${startDate}_to_${endDate}.xls`);
}

function buildMisRows(agentRows, dateLabels) {
  const rows = agentRows.map((agent, index) => {
    const dayMap = new Map((agent.trend || []).map((day) => [day.date, day.totalCalls || 0]));
    const counts = dateLabels.map((date) => dayMap.get(date) || 0);
    return {
      sno: index + 1,
      bmName: agent.agentName,
      counts,
      total: counts.reduce((sum, count) => sum + count, 0),
    };
  });
  const columnTotals = dateLabels.map((_, idx) => rows.reduce((sum, row) => sum + row.counts[idx], 0));
  const grandTotal = rows.reduce((sum, row) => sum + row.total, 0);
  return { rows, columnTotals, grandTotal };
}

function exportYtdExcel(rows) {
  const totals = rows.reduce(
    (acc, row) => {
      acc.underDeveloped += row.underDeveloped || 0;
      acc.developing += row.developing || 0;
      acc.proficient += row.proficient || 0;
      acc.expert += row.expert || 0;
      acc.total += row.totalCalls || 0;
      return acc;
    },
    { underDeveloped: 0, developing: 0, proficient: 0, expert: 0, total: 0 }
  );

  const header = ["Sno", "BM name", "Under Developed", "Developing", "Proficient", "Expert", "Total"];
  const tableRows = [
    header,
    ...rows.map((row, index) => [
      index + 1,
      row.agentName,
      row.underDeveloped || 0,
      row.developing || 0,
      row.proficient || 0,
      row.expert || 0,
      row.totalCalls || 0,
    ]),
    ["", "Total", totals.underDeveloped, totals.developing, totals.proficient, totals.expert, totals.total],
  ];

  const html = `
    <html>
      <head><meta charset="utf-8" /></head>
      <body>
        <table border="1">
          ${tableRows
            .map(
              (row, rowIndex) =>
                `<tr>${row
                  .map((cell) => `<${rowIndex === 0 ? "th" : "td"}>${escapeCell(cell)}</${rowIndex === 0 ? "th" : "td"}>`)
                  .join("")}</tr>`
            )
            .join("")}
        </table>
      </body>
    </html>
  `;
  const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
  saveAs(blob, `YTD_Report_${formatLocalDate(new Date())}.xls`);
}

function YtdReport({ rows }) {
  if (!rows.length) return null;
  const totals = rows.reduce(
    (acc, row) => {
      acc.underDeveloped += row.underDeveloped || 0;
      acc.developing += row.developing || 0;
      acc.proficient += row.proficient || 0;
      acc.expert += row.expert || 0;
      acc.total += row.totalCalls || 0;
      return acc;
    },
    { underDeveloped: 0, developing: 0, proficient: 0, expert: 0, total: 0 }
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1.5}>
        <Typography fontWeight={800} fontSize={15} color="#202832">
          YTD Report
        </Typography>
        <Button
          variant="outlined"
          onClick={() => exportYtdExcel(rows)}
          sx={{ textTransform: "none", fontWeight: 800, borderRadius: "12px" }}
        >
          Export YTD Excel
        </Button>
      </Box>
      <TableContainer sx={{ border: "1px solid #dde3eb", borderRadius: 2, overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#2d3748" }}>
              {["Sno", "BM name", "Under Developed", "Developing", "Proficient", "Expert", "Total"].map((head) => (
                <TableCell key={head} sx={{ color: "#fff", fontWeight: 800, fontSize: 12, whiteSpace: "nowrap" }}>
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.agentId || row.agentName} sx={{ bgcolor: index % 2 === 0 ? "#fff" : "#f8fafc" }}>
                <TableCell>{index + 1}</TableCell>
                <TableCell sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>{row.agentName}</TableCell>
                <TableCell>{row.underDeveloped || ""}</TableCell>
                <TableCell>{row.developing || ""}</TableCell>
                <TableCell>{row.proficient || ""}</TableCell>
                <TableCell>{row.expert || ""}</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>{row.totalCalls}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: "#f1f5f9" }}>
              <TableCell />
              <TableCell sx={{ fontWeight: 900 }}>Total</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>{totals.underDeveloped || ""}</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>{totals.developing || ""}</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>{totals.proficient || ""}</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>{totals.expert || ""}</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>{totals.total}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function WeightedAverageReport({ rows }) {
  if (!rows.length) return null;
  return (
    <Box mb={3}>
      <Typography fontWeight={800} fontSize={15} color="#202832" mb={1.5}>
        Weighted Average Report
      </Typography>
      <TableContainer sx={{ border: "1px solid #dde3eb", borderRadius: 2, overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#2d3748" }}>
              {["BM Name", "Total Calls", "Calls After 10th July (65%)", `Older Calls Before ${TRAINING_CUTOFF_DATE} (35%)`, "Weighted Score"].map((head) => (
                <TableCell key={head} sx={{ color: "#fff", fontWeight: 800, fontSize: 12 }}>
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.agentName} sx={{ bgcolor: index % 2 === 0 ? "#fff" : "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 800 }}>{row.agentName}</TableCell>
                <TableCell>{row.totalCalls}</TableCell>
                <TableCell>{row.recentCalls ? `${row.recentScore.toFixed(1)}% (${row.recentCalls} calls)` : "N/A"}</TableCell>
                <TableCell>{row.olderCalls ? `${row.olderScore.toFixed(1)}% (${row.olderCalls} calls)` : "N/A"}</TableCell>
                <TableCell sx={{ fontWeight: 900 }}>{`${row.weightedScore.toFixed(1)}%`}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography fontSize={11} color="#64748b" mt={0.75}>
        Weighting logic: calls after 10th July carry 65%; older calls before {TRAINING_CUTOFF_DATE} carry 35%.
      </Typography>
    </Box>
  );
}

function MisReport({ agentRows, startDate, endDate }) {
  const misStartDate = getFirstCallDate(agentRows, startDate);
  const dateLabels = getDateLabels(misStartDate, endDate);
  const { rows, columnTotals, grandTotal } = buildMisRows(agentRows, dateLabels);
  if (!rows.length) return null;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1.5}>
        <Typography fontWeight={800} fontSize={15} color="#202832">
          MIS Report
        </Typography>
        <Button
          variant="outlined"
          onClick={() => exportMisExcel(agentRows, misStartDate, endDate)}
          sx={{ textTransform: "none", fontWeight: 800, borderRadius: "12px" }}
        >
          Export MIS Excel
        </Button>
      </Box>
      <TableContainer sx={{ border: "1px solid #dde3eb", borderRadius: 2, overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: Math.max(720, dateLabels.length * 90 + 220) }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#2d3748" }}>
              {["Sno", "BM name", ...dateLabels.map(formatExcelDate), "Total"].map((head) => (
                <TableCell key={head} sx={{ color: "#fff", fontWeight: 800, fontSize: 12, whiteSpace: "nowrap" }}>
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={row.bmName} sx={{ bgcolor: index % 2 === 0 ? "#fff" : "#f8fafc" }}>
                <TableCell>{row.sno}</TableCell>
                <TableCell sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>{row.bmName}</TableCell>
                {row.counts.map((count, idx) => (
                  <TableCell key={dateLabels[idx]}>{count || ""}</TableCell>
                ))}
                <TableCell sx={{ fontWeight: 900 }}>{row.total}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: "#f1f5f9" }}>
              <TableCell />
              <TableCell sx={{ fontWeight: 900 }}>Total</TableCell>
              {columnTotals.map((total, idx) => (
                <TableCell key={dateLabels[idx]} sx={{ fontWeight: 900 }}>{total || ""}</TableCell>
              ))}
              <TableCell sx={{ fontWeight: 900 }}>{grandTotal}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function Performance() {
  const token = localStorage.getItem("token");
  const decodedToken = token ? jwtDecode(token) : {};
  const [payloadData, setPayloadData] = useState();
  const [trendDetails, setTrendDetails] = useState({});
  const [activeDateRange, setActiveDateRange] = useState(getDefaultDateRange());
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const lastFetchKeyRef = useRef("");

  const isNipponCompany = decodedToken?.companyId === NIPPON_COMPANY_ID;
  const agentRows = useMemo(
    () => (trendDetails?.agentTrend || []).filter((agent) => (agent.totalCalls || 0) > 0),
    [trendDetails]
  );
  const totalCalls = agentRows.reduce((sum, agent) => sum + (agent.totalCalls || 0), 0);
  const avgScore = agentRows.length
    ? agentRows.reduce((sum, agent) => sum + (agent.averageCallScore || 0), 0) / agentRows.length
    : 0;
  const topAgent = [...agentRows].sort((a, b) => (b.averageCallScore || 0) - (a.averageCallScore || 0))[0];
  const weightedRows = useMemo(() => buildWeightedRows(agentRows), [agentRows]);

  const isFilter = {
    date: true,
    filter: false,
    callDuration: false,
    callScore: false,
  };

  useEffect(() => {
    const fallbackRange = getDefaultDateRange();
    const nextRange = {
      startDate: payloadData?.filters?.startDate || fallbackRange.startDate,
      endDate: payloadData?.filters?.endDate || fallbackRange.endDate,
    };
    setActiveDateRange(nextRange);
    const fetchKey = `${decodedToken?.companyId || ""}:${nextRange.startDate}:${nextRange.endDate}`;

    if (!isNipponCompany) {
      setTrendDetails({});
      setLoading(false);
      return;
    }

    if (lastFetchKeyRef.current === fetchKey) {
      return;
    }
    lastFetchKeyRef.current = fetchKey;

    setLoading(true);
    axiosInstance
      .post(
        apiEndPointsConfig.interactionTrendAnalytics,
        {
          companyId: decodedToken?.companyId,
          startDate: nextRange.startDate,
          endDate: nextRange.endDate,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => setTrendDetails(res?.data?.data || {}))
      .catch((error) => {
        console.error("Error while fetching performance details:", error);
        setTrendDetails({});
      })
      .finally(() => setLoading(false));
  }, [payloadData, decodedToken?.companyId, isNipponCompany, token]);

  return (
    <ParentWrapper sx={{ backgroundColor: "#FFFFFF" }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 1, mb: 2 }}>
        <CommonFilter
          setPayloadData={setPayloadData}
          storageName="managerPerformance"
          isFilter={isFilter}
          defaultDateRange="month"
        />
      </Box>

      {loading ? (
        <Loader />
      ) : !isNipponCompany ? (
        <Box sx={{ border: "1px solid #dde3eb", borderRadius: 2, p: 3, bgcolor: "#f8fafc" }}>
          <Typography fontWeight={800} color="#202832">
            Performance report is not enabled for this company.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ borderBottom: "1px solid #dde3eb", mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(event, value) => setActiveTab(value)}
              sx={{
                "& .MuiTab-root": { textTransform: "none", fontWeight: 800 },
                "& .Mui-selected": { color: "#7c3aed !important" },
                "& .MuiTabs-indicator": { bgcolor: "#7c3aed" },
              }}
            >
              <Tab label="Agent Performance Report" />
              <Tab label="Weighted Average Report" />
              <Tab label="MIS Report" />
              <Tab label="YTD Report" />
            </Tabs>
          </Box>

          {agentRows.length === 0 ? (
            <Box display="flex" alignItems="center" justifyContent="center" minHeight={180} color="#999" fontWeight={700}>
              <CircularProgress size={20} sx={{ mr: 1.5, color: "#7C3AED" }} />
              No agent performance data available for the selected period.
            </Box>
          ) : activeTab === 0 ? (
            <>
              <Grid container spacing={2} mb={3}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <SummaryCard label="Agents Evaluated" value={agentRows.length} helper="With calls in selected period" />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <SummaryCard label="Total Calls" value={totalCalls} helper={`${activeDateRange.startDate} to ${activeDateRange.endDate}`} />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <SummaryCard label="Avg Agent Score" value={`${avgScore.toFixed(1)}%`} helper="Period average across agents" />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <SummaryCard
                    label="Top Performer"
                    value={topAgent?.agentName || "N/A"}
                    helper={topAgent ? `${(topAgent.averageCallScore || 0).toFixed(1)}% average` : "No calls found"}
                  />
                </Grid>
              </Grid>
              <AgentReports
                trendDetails={trendDetails}
                startDate={activeDateRange.startDate}
                endDate={activeDateRange.endDate}
                token={token}
                companyId={decodedToken?.companyId}
              />
            </>
          ) : activeTab === 1 ? (
            <WeightedAverageReport rows={weightedRows} />
          ) : activeTab === 2 ? (
            <MisReport agentRows={agentRows} startDate={activeDateRange.startDate} endDate={activeDateRange.endDate} />
          ) : (
            <YtdReport rows={trendDetails?.ytdReport || []} />
          )}
        </>
      )}
    </ParentWrapper>
  );
}
